import { useCallback, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEnv } from "@app.automotus.io/components/hooks/useEnv";
import { AccountContext } from "@app.automotus.io/components/context/AccountContext";
import { ACCOUNT_CONTEXT_ID_HEADER_NAME } from "common/constants";

export interface UseApiClientResult {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body: unknown) => Promise<T>;
}

/**
 * Thrown for a non-2xx response, with `message` set to the API's own error text when the body
 * matches its `{error: {message, code}}` convention (see the api repo's controller/errorhandlers.go)
 * -- distinct from a plain Error so callers can choose to show this message directly to the user
 * (it's written to be client-facing) while still falling back to a generic message for anything
 * else that can throw here (a stalled upload, an unexpected exception).
 */
export class ApiError extends Error {}

// A stalled mobile connection never fires a fetch rejection on its own -- it just hangs. This
// backs both the photo-upload-url presign call and the final POST /v1/enforcement_intents
// submit, so without a timeout, a connection going quiet on either leaves the form stuck in
// isSubmitting with both buttons disabled and no way to recover short of reloading (which loses
// the filled-out form) -- the same failure mode useEvidencePhotoUpload.ts's own S3 PUT timeout
// exists to prevent. Exported and reused there too, for one consistent "how long we tolerate a
// stalled connection" number across the whole mobile capture flow, rather than two independently
// hardcoded constants kept in sync by comment alone.
export const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Calls the Go API directly (bypassing Hasura/Apollo), for endpoints with no Hasura Action --
 * e.g. the mobile citation-capture endpoints from AUT-5574, which are deliberately API-internal
 * (see AUT-5642: enforcement.citation_photo is "purely an API-internal correlation table...
 * no GraphQL consumer needs it"). Mirrors the existing direct-fetch pattern already used by
 * useDocumentFetch.ts (Auth0 token) and ComingSoon.tsx (useEnv().apiEndpoint).
 *
 * Every endpoint this hook is meant to call requires the enforcement_intents.write scope, so
 * unlike useDocumentFetch.ts (which serves both auth-required and public URLs and falls back to
 * an unauthenticated request), this hook always requires an authenticated session -- it throws
 * rather than silently sending a request with no token.
 */
export function useApiClient(): UseApiClientResult {
  const { apiEndpoint } = useEnv();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  // Read directly rather than via useAccountContext() (which throws with no provider mounted)
  // so this degrades gracefully wherever this hook is used without an AccountContextProvider in
  // the tree -- same reasoning, and same header, as AuthorizedApolloProvider's GraphQL requests.
  // Without this, an operator who has switched to a parent/child account would have this
  // client's REST calls silently run against their own root account instead.
  const { contextId } = useContext(AccountContext);

  const request = useCallback(
    async <T>(path: string, init: RequestInit): Promise<T> => {
      if (!isAuthenticated) {
        throw new Error("useApiClient: cannot make a request without an authenticated session");
      }

      const token = await getAccessTokenSilently();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      let response: Response;
      try {
        response = await fetch(`${apiEndpoint}${path}`, {
          ...init,
          signal: controller.signal,
          headers: {
            ...init.headers,
            Authorization: `Bearer ${token}`,
            ...(contextId && { [ACCOUNT_CONTEXT_ID_HEADER_NAME]: contextId }),
          },
        });
      } catch (err) {
        // A timeout-triggered abort rejects with a DOMException named "AbortError", never a
        // TypeError -- deliberately re-thrown as a plain Error (not ApiError, since the API never
        // actually responded) so it falls through GenerateCitation's error handling into the same
        // generic, recoverable message as any other unexpected failure, rather than the
        // network-specific "No connection" one that's reserved for a real TypeError.
        if (err instanceof DOMException && err.name === "AbortError") {
          throw new Error(`API request to ${path} timed out`);
        }
        throw err;
      } finally {
        clearTimeout(timeoutId);
      }

      if (!response.ok) {
        const bodyText = await response.text();
        console.error(`API request to ${path} failed (${response.status}): ${bodyText}`);
        let message = `Something went wrong (status ${response.status}). Try again.`;
        try {
          const parsedBody = JSON.parse(bodyText) as { error?: { message?: string } };
          if (parsedBody.error?.message) {
            message = parsedBody.error.message;
          }
        } catch {
          // Body wasn't the {error: {message, code}} shape -- already logged above, fall back
          // to the generic message rather than showing a raw (possibly HTML) response body.
        }
        throw new ApiError(message);
      }

      return response.json() as Promise<T>;
    },
    [apiEndpoint, getAccessTokenSilently, isAuthenticated, contextId],
  );

  const get = useCallback(<T>(path: string) => request<T>(path, { method: "GET" }), [request]);

  const post = useCallback(
    <T>(path: string, body: unknown) =>
      request<T>(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    [request],
  );

  return { get, post };
}

export default useApiClient;
